import React, { useMemo, useState, useEffect } from "react";
import { apiPost, apiGet } from "../api";
import type { Locale } from "../i18n";
import { t } from "../i18n";
import { Dropdown, type DropdownOption } from "../components/Dropdown";
import { BtnSpinner } from "../components/BtnSpinner";
import { generateCountryCodesList } from "../data/generateCountries";
import type { CountryData } from "../data/generateCountries";
import { formatPhoneNumber, getPhonePlaceholder, getMaxDigits } from "../data/phoneFormats";

type RecoveryMethod = "email" | "sms" | "card" | "codes" | "identity";
type RecoveryStep = "email_entry" | "active_check" | "method_select" | "verify_method" | "security_question" | "timer" | "success" | "error";
type RecoverySessionStatus = "pending" | "ready" | "completed" | "cancelled" | "expired" | "locked" | "unlocked" | "admin_queue" | "already_in_progress";

interface RecoverySession {
  sessionToken: string;
  step: string;
  status?: RecoverySessionStatus;
  delaySeconds?: number;
  unlocksAt?: string;
  expiresAt?: string;
  adminQueue?: boolean;
}

interface SecurityQuestion {
  configured: boolean;
  questionId?: number;
  questionText?: string;
}


// ─── Backend response shapes (see recovery-flow-reconciliation.md §3) ───
interface StartResponse {
  sessionToken: string;
  step?: string;
  status?: string;
  delaySeconds?: number;
  unlocksAt?: string;
  maskedContact?: string;
  adminQueue?: boolean;
  expiresAt?: string;
}
interface VerifyMethodResponse { ok: boolean; step?: string }
interface StatusResponse { status: string; unlocksAt?: string }
interface QuestionResponse { configured: boolean; questionId?: number; questionText?: string }
// Structured error fields the API's flattened `error` string drops; read off ApiErr.body.
interface RecoveryErrorBody {
  error?: string;
  remainingAttempts?: number;
  attemptsLeft?: number;
  lockedUntil?: string;
  lockRemainingSeconds?: number;
  failureCount?: number;
}

// Map frontend method → the actual backend verify route (names differ). Only the
// methods that verify a real secret are listed: recovery codes (consumed) and SMS
// (OTP to the on-file phone). email/card/identity are gated in the UI and absent
// here — notably verify-email-method is a no-op stub that proves nothing, so email
// must not reach a verify call until the magic-link flow is built.
const VERIFY_ROUTES: Record<string, string> = {
  codes: "/v1/recovery/verify-recovery-codes",
  sms: "/v1/recovery/verify-sms-method"
};

// Methods with time delays (show SMS opt-in)
const METHODS_WITH_DELAY = new Set(["email", "sms", "card", "identity"]);

// Flag Icon Component
function FlagIcon({ src, alt, size = "24px" }: { src: string; alt: string; size?: string }) {
  return (
    <img
      src={src}
      alt={alt}
      style={{
        width: size,
        height: size,
        marginRight: "0.5rem",
        verticalAlign: "middle",
        borderRadius: "2px"
      }}
    />
  );
}

// Geolocation country code map (ISO code to country code used in filenames)
const GEOLOCATION_COUNTRY_MAP: { [key: string]: string } = {
  US: "US",
  CA: "CA",
  GB: "GB",
  FR: "FR",
  DE: "DE",
  IT: "IT",
  ES: "ES",
  NL: "NL",
  CH: "CH",
  AT: "AT",
  SE: "SE",
  DK: "DK",
  NO: "NO",
  FI: "FI",
  BE: "BE",
  IE: "IE",
  AU: "AU",
  NZ: "NZ",
  JP: "JP",
  CN: "CN",
  IN: "IN",
  BR: "BR",
  MX: "MX"
};

export function AccountRecoveryView(props: { locale: Locale }) {
  // Step management
  const [step, setStep] = useState<RecoveryStep>("email_entry");
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);

  // Email entry (Step 1)
  const [email, setEmail] = useState("");
  const [emailSubmitted, setEmailSubmitted] = useState(false);

  // Method Selection (Step 2b - if no active recovery)
  const [method, setMethod] = useState<RecoveryMethod>("codes");
  const [notifyWhenReady, setNotifyWhenReady] = useState(false);
  const [selectedCountryCode, setSelectedCountryCode] = useState("US");
  const [phoneNumber, setPhoneNumber] = useState("");

  // Method Verification (Step 3)
  const [methodVerificationData, setMethodVerificationData] = useState("");
  const [cardExpiry, setCardExpiry] = useState({ mm: "", yyyy: "" });

  // Security Question (Step 4)
  const [securityQuestion, setSecurityQuestion] = useState<SecurityQuestion | null>(null);
  const [securityAnswer, setSecurityAnswer] = useState("");

  // Timer Screen (Step 5)
  const [unlocksAt, setUnlocksAt] = useState<string | null>(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [sessionStatus, setSessionStatus] = useState<RecoverySessionStatus>("pending");

  // Code failure tracking
  const [codeLockedUntil, setCodeLockedUntil] = useState<string | null>(null);
  const [codeLockRemaining, setCodeLockRemaining] = useState(0);
  const [remainingAttempts, setRemainingAttempts] = useState<number | null>(null);

  // UI state
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Country codes list (loaded dynamically)
  const [countryCodesList, setCountryCodesList] = useState<CountryData[]>([]);

  // Geolocation detection for suggestions
  const [detectedCountries, setDetectedCountries] = useState<string[]>([]);

  // Computed values
  const canSubmitEmail = useMemo(() => email.trim().length > 3, [email]);
  const canSubmitMethodProof = useMemo(() => {
    if (method === "email") return true; // link-based; nothing to type
    if (method === "codes") return methodVerificationData.trim().length > 0;
    if (method === "card") return methodVerificationData.length === 4 && Boolean(cardExpiry.mm) && Boolean(cardExpiry.yyyy);
    return methodVerificationData.length >= 4; // sms / identity
  }, [method, methodVerificationData, cardExpiry]);

  const methodOptions: DropdownOption<RecoveryMethod>[] = useMemo(
    () => {
      const comingSoon = t(props.locale, "recovery.method.comingSoon");
      return [
        { value: "codes", label: t(props.locale, "recovery.method.codes") },
        { value: "sms", label: t(props.locale, "recovery.method.sms") },
        // Gated (disabled) — no trustworthy backend verification yet:
        //  - email: verify-email-method is a no-op stub (needs magic-link via /action)
        //  - card / identity: Stripe card-auth + Stripe Identity not wired
        { value: "email", label: `${t(props.locale, "recovery.method.email")} (${comingSoon})`, isDisabled: true },
        { value: "card", label: `${t(props.locale, "recovery.method.card")} (${comingSoon})`, isDisabled: true },
        { value: "identity", label: `${t(props.locale, "recovery.method.identity")} (${comingSoon})`, isDisabled: true }
      ];
    },
    [props.locale]
  );

  // Validate phone number format (basic: at least 7 digits)
  const isValidPhone = useMemo(() => {
    const digits = phoneNumber.replace(/\D/g, "");
    return digits.length >= 7 && digits.length <= 15;
  }, [phoneNumber]);

  // Create country code dropdown options with short labels for closed state
  const countryCodeOptions = useMemo(() => {
    if (countryCodesList.length === 0) return [];

    // Build suggested countries: detected geo (max 2) + US as default (max 3 total)
    const suggestedCodes: string[] = [];

    // Add detected countries first (max 2)
    for (const code of detectedCountries.slice(0, 2)) {
      if (countryCodesList.find(c => c.countryCode === code) && !suggestedCodes.includes(code)) {
        suggestedCodes.push(code);
      }
    }

    // Always add US if not already present and room available
    if (suggestedCodes.length < 3 && !suggestedCodes.includes("US")) {
      suggestedCodes.push("US");
    }

    // Helper to create option entry
    const makeOption = (country: CountryData, group: string, valueSuffix?: string) => {
      const countryName = country.label.split("(")[0].trim();
      // Use suffix to create unique React keys for suggested duplicates
      const value = valueSuffix ? `${country.countryCode}::${valueSuffix}` : country.countryCode;
      return {
        value,
        shortLabel: (
          <span style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
            <FlagIcon src={country.flagPath} alt={country.countryCode} size="18px" />
            <span>{country.code}</span>
          </span>
        ),
        label: (
          <span style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <FlagIcon src={country.flagPath} alt={country.countryCode} size="18px" />
            <span>{country.code}</span>
            <span>{countryName}</span>
          </span>
        ),
        group
      };
    };

    const options: Array<DropdownOption<string>> = [];

    // Add suggested countries at top
    for (const code of suggestedCodes) {
      const country = countryCodesList.find(c => c.countryCode === code);
      if (country) {
        options.push(makeOption(country, "Suggested", "suggested"));
      }
    }

    // Separator between suggested and full list
    if (suggestedCodes.length > 0) {
      options.push({
        value: "__separator__",
        label: <div style={{ height: "1px", background: "var(--line)", margin: "4px 0" }} />,
        shortLabel: "",
        isDisabled: true
      });
    }

    // Add all countries with region grouping
    for (const c of countryCodesList) {
      options.push(makeOption(c, c.region ?? "Other"));
    }

    return options;
  }, [countryCodesList, detectedCountries]);

  // Load country codes from manifest
  useEffect(() => {
    const loadCountries = async () => {
      try {
        const response = await fetch("/icons/flags/manifest.json");
        if (response.ok) {
          const data = await response.json();
          const countries = generateCountryCodesList(data.flags || []);
          setCountryCodesList(countries);
        }
      } catch (err) {
        console.error("Failed to load country codes:", err);
      }
    };
    loadCountries();
  }, []);

  // Initialize email from query parameter
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const emailParam = params.get("email");
    if (emailParam) {
      setEmail(emailParam);
    }
  }, []);

  // Detect geolocation once countries are loaded
  useEffect(() => {
    if (countryCodesList.length === 0) return;

    const detectCountryCode = async () => {
      try {
        const response = await fetch("https://ipinfo.io/json");
        const data = await response.json();

        const detected: string[] = [];

        // Add primary country if detected
        if (data.country && countryCodesList.find((c) => c.countryCode === data.country)) {
          detected.push(data.country);
          setSelectedCountryCode(data.country);
        }

        // Optionally add additional countries from geolocation data
        // For now, we'll just use the primary country
        setDetectedCountries(detected);
      } catch (err) {
        console.debug("Could not detect country code, using default: US");
        setDetectedCountries([]);
      }
    };

    detectCountryCode();
  }, [countryCodesList]);


  // Timer countdown effect for unlock
  useEffect(() => {
    if (step !== "timer" || !unlocksAt) return;

    const interval = setInterval(() => {
      const now = new Date().getTime();
      const unlockTime = new Date(unlocksAt).getTime();
      const remaining = Math.max(0, Math.floor((unlockTime - now) / 1000));
      setTimeRemaining(remaining);
    }, 1000);

    return () => clearInterval(interval);
  }, [step, unlocksAt]);

  // Code lockout countdown
  useEffect(() => {
    if (!codeLockedUntil) return;

    const interval = setInterval(() => {
      const now = new Date().getTime();
      const lockTime = new Date(codeLockedUntil).getTime();
      const remaining = Math.max(0, Math.floor((lockTime - now) / 1000 / 60));
      setCodeLockRemaining(remaining);
    }, 1000);

    return () => clearInterval(interval);
  }, [codeLockedUntil]);

  // Poll status endpoint while on timer screen
  useEffect(() => {
    if (step !== "timer" || !sessionToken) return;

    const pollStatus = async () => {
      const result = await apiGet<StatusResponse>(`/v1/recovery/status/${sessionToken}`);
      if (!result.ok) return; // transient — keep polling
      const status = result.data.status;
      if (status === "pending") {
        setSessionStatus("pending");
        if (result.data.unlocksAt) setUnlocksAt(result.data.unlocksAt);
        return;
      }
      if (status === "unlocked" || status === "ready") {
        // Model A: the backend scheduler sends a reset link to the owner's
        // pre-approved destination(s) at auto-resolve. Nothing for the browser
        // to do — the timer screen is the terminal state; the owner completes
        // the reset by opening the link in their email/SMS.
        setSessionStatus(status === "ready" ? "ready" : "unlocked");
      }
      // completed / cancelled / admin_queue: no client transition here
    };

    // Poll every 30 seconds
    const interval = setInterval(pollStatus, 30 * 1000);
    // Also poll immediately on mount
    pollStatus();

    return () => clearInterval(interval);
  }, [step, sessionToken]);

  // Step 1: Submit email
  const handleSubmitEmail = async () => {
    if (!canSubmitEmail) return;
    // No active-recovery precheck: the backend folds that into /start, which
    // returns status "already_in_progress" when a session already exists.
    setError(null);
    setEmailSubmitted(true);
    setStep("method_select");
  };

  // Start new recovery with selected method
  const handleStartNewRecovery = async () => {
    setBusy(true);
    setError(null);

    // Only include notifyWhenReady if method has a delay and phone is provided
    const hasDelay = METHODS_WITH_DELAY.has(method);
    const shouldNotify = hasDelay && notifyWhenReady && isValidPhone;
    const selectedCountry = countryCodesList.find((c) => c.countryCode === selectedCountryCode);
    const phoneCode = selectedCountry?.code || "+1";

    const result = await apiPost<StartResponse>("/v1/recovery/start", {
      email,
      method,
      notifyWhenReady: shouldNotify,
      phone: shouldNotify ? `${phoneCode}${phoneNumber.replace(/\D/g, "")}` : undefined
    });

    if (!result.ok) {
      setError(result.error || t(props.locale, "recovery.error.start_failed"));
      setBusy(false);
      return;
    }

    const data = result.data;
    setSessionToken(data.sessionToken);

    // Shape A — an active session already exists → straight to the timer.
    if (data.status === "already_in_progress") {
      if (data.unlocksAt) setUnlocksAt(data.unlocksAt);
      setStep("timer");
      setBusy(false);
      return;
    }

    // Shape B — new session (or anti-enumeration decoy, which omits
    // expiresAt — tolerate its absence).
    if (data.expiresAt) setExpiresAt(data.expiresAt);
    // Real-success returns delaySeconds (not unlocksAt) — derive the unlock time;
    // instant (0s) methods have no delay.
    setUnlocksAt(data.delaySeconds && data.delaySeconds > 0
      ? new Date(Date.now() + data.delaySeconds * 1000).toISOString()
      : null);

    // SMS needs its OTP actually sent before the user can enter it.
    if (method === "sms") {
      const sent = await apiPost("/v1/recovery/send-sms-otp", { sessionToken: data.sessionToken });
      if (!sent.ok) {
        setError(sent.error || t(props.locale, "recovery.error.sms_send_failed"));
      }
    }

    setStep("verify_method");
    setBusy(false);
  };

  // Verify recovery method
  const handleVerifyMethod = async () => {
    if (!canSubmitMethodProof || !sessionToken) return;

    const route = VERIFY_ROUTES[method];
    if (!route) {
      // card/identity are gated in the UI; defensive guard against a stray call.
      setError(t(props.locale, "recovery.error.method_unavailable"));
      return;
    }

    setBusy(true);
    setError(null);

    // email trusts the session (link already sent); codes/sms carry a code.
    const payload: Record<string, unknown> = { sessionToken };
    if (method !== "email") payload.code = methodVerificationData;

    const result = await apiPost<VerifyMethodResponse>(route, payload);

    if (!result.ok) {
      const body = (result.body ?? {}) as RecoveryErrorBody;
      const attempts = body.remainingAttempts ?? body.attemptsLeft;
      if (attempts !== undefined) setRemainingAttempts(attempts);
      if (body.lockedUntil) setCodeLockedUntil(body.lockedUntil);
      setError(result.error || t(props.locale, "recovery.error.verification_failed"));
      setBusy(false);
      return;
    }

    // Method verified. Pull the security question if one is configured.
    const questionRes = await apiGet<QuestionResponse>(`/v1/recovery/question/${encodeURIComponent(email)}`);
    if (questionRes.ok && questionRes.data.configured) {
      setSecurityQuestion(questionRes.data);
      setStep("security_question");
    } else {
      // No question (or lookup failed) → timer. The timer is the terminal
      // state in Model A; the backend sends the reset link to the owner's channel.
      setStep("timer");
    }
    setBusy(false);
  };

  // Submit security answer
  const handleSecurityAnswer = async (skip = false) => {
    if (!sessionToken) return;
    if (!skip && !securityAnswer.trim()) return;

    setBusy(true);
    setError(null);

    const result = await apiPost("/v1/recovery/submit-security-answer", {
      sessionToken,
      answer: skip ? undefined : securityAnswer,
      skipped: skip
    });

    if (!result.ok) {
      setError(result.error || t(props.locale, "recovery.error.answer_failed"));
      setBusy(false);
      return;
    }

    // Always go to the timer — the terminal state in Model A.
    setStep("timer");
    setBusy(false);
  };

  // Cancel recovery
  const handleCancelRecovery = async () => {
    if (!sessionToken || !confirm(t(props.locale, "recovery.timer.cancel_confirm"))) return;

    setBusy(true);
    const result = await apiPost(`/v1/recovery/cancel/${sessionToken}`, {});
    if (!result.ok) {
      setError(result.error || t(props.locale, "recovery.error.cancel_failed"));
      setBusy(false);
      return;
    }
    setStep("email_entry");
    setSessionToken(null);
    setError(null);
    setEmailSubmitted(false);
    setBusy(false);
  };

  // Format time display
  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    }
    return `${minutes}m ${secs}s`;
  };

  // ======== RENDER SCREENS ========

  // Screen 1: Email Entry Only
  if (step === "email_entry") {
    return (
      <div className="recoveryContainer">
        <h1>{t(props.locale, "recovery.title")}</h1>
        <p>{t(props.locale, "recovery.description")}</p>

        {error && <div className="errorBox">{error}</div>}

        <div className="formGroup">
          <label>{t(props.locale, "recovery.label.email")}</label>
          <input
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError(null);
            }}
            onKeyPress={(e) => {
              if (e.key === "Enter" && canSubmitEmail) {
                handleSubmitEmail();
              }
            }}
            placeholder={t(props.locale, "recovery.placeholder.email")}
            disabled={busy}
            autoFocus
          />
        </div>

        <button onClick={handleSubmitEmail} disabled={!canSubmitEmail || busy} className="primaryBtn">
          <BtnSpinner loading={busy}>{busy ? t(props.locale, "common.loading") : t(props.locale, "common.continue")}</BtnSpinner>
        </button>

        <p className="centerText">
          <a href="/login">{t(props.locale, "recovery.back_to_login")}</a>
        </p>
      </div>
    );
  }

  // Screen 2b: Method Selection (if no active recovery)
  if (step === "method_select") {
    const hasDelay = METHODS_WITH_DELAY.has(method);

    return (
      <div className="recoveryContainer">
        <h1>{t(props.locale, "recovery.title")}</h1>
        <p>{t(props.locale, "recovery.description")}</p>

        {error && <div className="errorBox">{error}</div>}

        <div className="formGroup">
          <label>{t(props.locale, "recovery.label.method")}</label>
          <Dropdown<RecoveryMethod>
            value={method}
            options={methodOptions}
            onChange={setMethod}
            ariaLabel={t(props.locale, "recovery.label.method")}
          />
        </div>

        {hasDelay && (
          <>
            <div className="formGroup checkboxGroup">
              <input
                type="checkbox"
                id="notifyWhenReady"
                checked={notifyWhenReady}
                onChange={(e) => setNotifyWhenReady(e.target.checked)}
                disabled={busy}
              />
              <label htmlFor="notifyWhenReady">{t(props.locale, "recovery.label.notify_sms")}</label>
              <small>{t(props.locale, "recovery.hint.notify_sms")}</small>
            </div>

            {notifyWhenReady && (
              <div className="phoneInputSection">
                <label className="phoneLabel">{t(props.locale, "recovery.label.phone_number")}</label>

                <div className="phoneInputGroup">
                  {/* Country Code Selector */}
                  <Dropdown
                    value={selectedCountryCode}
                    options={countryCodeOptions}
                    onChange={(v) => {
                      // Strip "::suggested" suffix from suggested item values
                      const code = v.includes("::") ? v.split("::")[0] : v;
                      setSelectedCountryCode(code);
                    }}
                    ariaLabel={t(props.locale, "recovery.label.country_code")}
                    widthPx={140}
                  />

                  {/* Phone Number Input */}
                  <input
                    type="tel"
                    value={formatPhoneNumber(phoneNumber, selectedCountryCode)}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, "");
                      const max = getMaxDigits(selectedCountryCode);
                      if (digits.length <= max) {
                        setPhoneNumber(digits);
                      }
                    }}
                    placeholder={getPhonePlaceholder(selectedCountryCode) || t(props.locale, "recovery.placeholder.phone_number")}
                    disabled={busy}
                    className="phoneInput"
                  />
                </div>

                <small className="phoneHint">{t(props.locale, "recovery.hint.phone_number")}</small>
                {phoneNumber && !isValidPhone && (
                  <small className="error">{t(props.locale, "recovery.error.invalid_phone")}</small>
                )}
              </div>
            )}
          </>
        )}

        <button onClick={handleStartNewRecovery} disabled={busy || (notifyWhenReady && !isValidPhone)} className="primaryBtn">
          <BtnSpinner loading={busy}>{busy ? t(props.locale, "common.loading") : t(props.locale, "recovery.start_recovery")}</BtnSpinner>
        </button>

        <p className="centerText">
          <a href="javascript:history.back()">{t(props.locale, "common.back")}</a>
        </p>
      </div>
    );
  }

  // Screen 3: Method Verification
  if (step === "verify_method") {
    return (
      <div className="recoveryContainer">
        <h1>{t(props.locale, `recovery.verify_${method}.title`)}</h1>

        {error && <div className="errorBox">{error}</div>}

        {remainingAttempts !== null && (
          <div className="warningBox">
            {remainingAttempts === 0
              ? t(props.locale, "recovery.code.locked")
              : t(props.locale, "recovery.code.attempts_remaining", { remaining: String(remainingAttempts) })}
          </div>
        )}

        {method === "codes" && (
          <div className="formGroup">
            <label>{t(props.locale, "recovery.label.recovery_code")}</label>
            <input
              type="text"
              value={methodVerificationData}
              onChange={(e) => setMethodVerificationData(e.target.value.toUpperCase())}
              placeholder={t(props.locale, "recovery.placeholder.recovery_code")}
              disabled={busy || remainingAttempts === 0}
              autoFocus
            />
          </div>
        )}

        {method === "card" && (
          <>
            <div className="formGroup">
              <label>{t(props.locale, "recovery.label.card_last4")}</label>
              <input
                type="text"
                maxLength={4}
                value={methodVerificationData}
                onChange={(e) => setMethodVerificationData(e.target.value.replace(/\D/g, ""))}
                placeholder="0000"
                disabled={busy}
                autoFocus
              />
            </div>
            <div className="formRow">
              <div className="formGroup">
                <label>{t(props.locale, "recovery.label.expiry_month")}</label>
                <input
                  type="text"
                  maxLength={2}
                  value={cardExpiry.mm}
                  onChange={(e) => setCardExpiry({ ...cardExpiry, mm: e.target.value.replace(/\D/g, "") })}
                  placeholder="MM"
                  disabled={busy}
                />
              </div>
              <div className="formGroup">
                <label>{t(props.locale, "recovery.label.expiry_year")}</label>
                <input
                  type="text"
                  maxLength={4}
                  value={cardExpiry.yyyy}
                  onChange={(e) => setCardExpiry({ ...cardExpiry, yyyy: e.target.value.replace(/\D/g, "") })}
                  placeholder="YYYY"
                  disabled={busy}
                />
              </div>
            </div>
          </>
        )}

        {/* Email recovery is link-based: the backend already sent the confirmation
            link on /start, so there is no code to type — just confirm and continue. */}
        {method === "email" && (
          <p>{t(props.locale, "recovery.email.link_sent")}</p>
        )}

        {method === "sms" && (
          <div className="formGroup">
            <label>{t(props.locale, "recovery.label.sms_code")}</label>
            <input
              type="text"
              value={methodVerificationData}
              onChange={(e) => setMethodVerificationData(e.target.value)}
              placeholder={t(props.locale, "recovery.placeholder.sms_code")}
              disabled={busy}
              autoFocus
            />
          </div>
        )}

        <button onClick={handleVerifyMethod} disabled={!canSubmitMethodProof || busy} className="primaryBtn">
          <BtnSpinner loading={busy}>{busy ? t(props.locale, "common.loading") : t(props.locale, "recovery.verify")}</BtnSpinner>
        </button>
      </div>
    );
  }

  // Screen 4: Security Question
  if (step === "security_question") {
    return (
      <div className="recoveryContainer">
        <h1>{t(props.locale, "recovery.security_question.title")}</h1>
        <p>{t(props.locale, "recovery.security_question.description")}</p>

        {error && <div className="errorBox">{error}</div>}

        {securityQuestion && (
          <div className="formGroup">
            <label>{securityQuestion.questionText}</label>
            <input
              type="text"
              value={securityAnswer}
              onChange={(e) => setSecurityAnswer(e.target.value)}
              placeholder={t(props.locale, "recovery.placeholder.answer")}
              disabled={busy}
              autoFocus
            />
          </div>
        )}

        <div className="buttonGroup">
          <button onClick={() => handleSecurityAnswer(false)} disabled={!securityAnswer.trim() || busy} className="primaryBtn">
            <BtnSpinner loading={busy}>{busy ? t(props.locale, "common.loading") : t(props.locale, "recovery.submit_answer")}</BtnSpinner>
          </button>
          <button onClick={() => handleSecurityAnswer(true)} disabled={busy} className="secondaryBtn">
            {t(props.locale, "recovery.skip_question")}
          </button>
        </div>
      </div>
    );
  }

  // Screen 5: Timer Screen (terminal state in Model A — link delivery happens server-side)
  if (step === "timer") {
    return (
      <div className="recoveryContainer">
        <h1>{t(props.locale, "recovery.timer.title")}</h1>

        {error && <div className="errorBox">{error}</div>}
        {successMessage && <div className="successBox">{successMessage}</div>}

        {/* Main Timer */}
        <div className="timerSection">
          <h2>{t(props.locale, "recovery.timer.security_delay")}</h2>
          <div className="mainTimer">{formatTime(timeRemaining)}</div>
          {/* Model A end-state message: link is sent to owner's channel after the delay */}
          <p>{t(props.locale, "recovery.timer.link_delivery_message")}</p>
        </div>

        {/* Expiry Timer */}
        {expiresAt && (
          <div className="expirySection">
            <small>{t(props.locale, "recovery.timer.expires", { date: new Date(expiresAt).toLocaleString() })}</small>
          </div>
        )}

        {/* Action Buttons */}
        <div className="buttonGroup">
          <button onClick={handleCancelRecovery} disabled={busy} className="secondaryBtn">
            {t(props.locale, "recovery.timer.cancel")}
          </button>
        </div>
      </div>
    );
  }

  // Screen 6: Success
  if (step === "success") {
    return (
      <div className="recoveryContainer">
        <h1>{t(props.locale, "recovery.success.title")}</h1>
        <div className="successBox">{successMessage}</div>
        <p>{t(props.locale, "recovery.success.redirecting")}</p>
      </div>
    );
  }

  return null;
}
